/**
 * **街の中のドット絵** (#424)。村・城の内部で使う専用パーツ。
 *
 * フィールドのパーツ (橋・街・山) を流用していたため、**道が橋・家が街・壁が山**に
 * 見えていた (実機で指摘)。内部マップ専用の絵を用意する。
 *
 * ## 地形名で引く
 *
 * 絵は `part:<index>` → **地形名** の順で探される。内部マップは独自のパーツ表を
 * 持てるが、`part:<index>` は**フィールドのパーツ表と番号空間を共有する**ので、
 * 内部で 8 番を使うとフィールドの 8 番 (たての橋) の絵が出てしまう。
 * そこで内部専用のパーツには**専用の地形名**を付け、その名前で絵を引く。
 *
 * 建物は 1 枚絵にできない (1 マス 16×16) ので、**屋根・壁・扉を積んで組む**。
 * 宿屋となんでも屋は扉の上に看板を置いて見分けられるようにする。
 */
import type { TileArtRecord } from './tile-art.js';

export const INTERIOR_ARTS: Record<string, TileArtRecord> = {
  // 石畳の床
  'floor-stone': {
    size: 16,
    palette: ['', '#b9b2a4', '#9c9488', '#d3cec3'],
    pixels:
      'AQECAQEBAQIBAQIBAQEBAgEDAQECAQEBAQIBAQEDAQECAQEBAQMBAgEBAQECAQEBAQEBAgEBAQECAQMBAQECAQECAQEBAQIBAQEBAgEBAQMBAQMBAgEBAwEBAgEBAgEBAQEBAQECAQEBAgEBAwEBAgIBAQMBAQECAQEBAQIBAQEBAQIBAQEDAQIBAQIBAQMBAQMBAQIBAQEBAwEBAQIBAQIBAQEBAgEDAQECAQEBAQIBAQEDAQEBAQIBAQEDAQEBAQIBAQECAQEBAQMBAgEBAQEBAQIBAwECAQEBAQECAQMDAQEBAQEBAQMBAgEBAQEBAQECAQECAQEBAQECAQECAQ==',
  },
  // 石の壁
  'wall-brick': {
    size: 16,
    palette: ['', '#9c9488', '#7d7466', '#2b2b38'],
    pixels:
      'AwMDAwMDAwMDAwMDAwMDAwMCAgIBAgICAwICAQICAgMDAgECAgIBAgMCAQICAQIDAwICAgECAgIDAgICAQICAwMDAwMDAwMDAwMDAwMDAwMCAgECAgIDAgIBAgICAwICAgECAgECAwIBAgICAQIDAgICAgECAgMCAgIBAgICAwIDAwMDAwMDAwMDAwMDAwMDAwICAQICAgMCAgIBAgICAwMCAQICAQIDAgECAgIBAgMDAgICAQICAwICAQICAgIDAwMDAwMDAwMDAwMDAwMDAwIBAgICAwICAQICAgMCAgECAgECAgMCAQICAgECAwICAwMDAwMDAwMDAwMDAwMDAw==',
  },
  // 屋根
  'roof': {
    size: 16,
    palette: ['', '#c0392b', '#96291d', '#e05a45', '#2b2b38'],
    pixels:
      'BAQEBAQEBAQEBAQEBAQEBAQDAwMDAwMDAwMDAwMDAwQEAwEBAQEBAQEBAQEBAQMEBAEBAgEBAQIBAQECAQEBBAQBAgEBAQIBAQECAQEBAgQEAQEBAgEBAQIBAQECAQEEBAIBAQECAQEBAgEBAQIBBAQBAQIBAQECAQEBAgEBAQQEAQIBAQECAQEBAgEBAQIEBAEBAQIBAQECAQEBAgEBBAQCAQEBAgEBAQIBAQECAQQEAQECAQEBAgEBAQIBAQEEBAECAQEBAgEBAQIBAQECBAQBAQECAQEBAgEBAQIBAQQEAgICAgICAgICAgICAgIEBAQEBAQEBAQEBAQEBAQEBA==',
  },
  // 家の壁 (窓つき)
  'house-wall': {
    size: 16,
    palette: ['', '#c9bfa8', '#e3dbc6', '#6b6152', '#2b2b38', '#4a9ad4', '#2b74c4'],
    pixels:
      'BAQEBAQEBAQEBAQEBAQEBAQBAQEBAQEBAQEBAQEBAQQEAQIBAQEBAQEBAQEBAgEEBAEBAQEBAQEBAQEBAQEBBAQBAQEDAwMDAwMBAQEBAQQEAQEBAwUFBQUDAQEBAQEEBAECAQMFBgYFAwEBAQIBBAQBAQEDBQUFBQMBAQEBAQQEAQEBAwMDAwMDAQEBAQEEBAEBAQEBAQEBAQEBAQEBBAQBAgEBAQEBAQEBAQECAQQEAQEBAQEBAQEBAQEBAQEEBAEBAQEBAQEBAQEBAQEBBAQBAgEBAQEBAQEBAQECAQQEAwMDAwMDAwMDAwMDAwMEBAQEBAQEBAQEBAQEBAQEBA==',
  },
  // 扉
  'door': {
    size: 16,
    palette: ['', '#c9bfa8', '#6b6152', '#7a4a26', '#4e2f16', '#e8c34a', '#2b2b38'],
    pixels:
      'BgYGBgYGBgYGBgYGBgYGBgYBAQEBAQEBAQEBAQEBAQYGAQEGBgYGBgYGBgYGAQEGBgEGBAQEBAQEBAQEBAYBBgYBBgQDAwMDAwMDAwQGAQYGAQYEAwQDAwMDBAMEBgEGBgEGBAMDAwMDAwMDBAYBBgYBBgQDAwMDAwMDAwQGAQYGAQYEAwMDAwUDAwMEBgEGBgEGBAMEAwMDAwQDBAYBBgYBBgQDAwMDAwMDAwQGAQYGAQYEAwMDAwMDAwMEBgEGBgEGBAQEBAQEBAQEBAYBBgYBAQYGBgYGBgYGBgYBAQYGAgICAgICAgICAgICAgIGBgYGBgYGBgYGBgYGBgYGBg==',
  },
  // 宿屋の看板 (ベッド)
  'sign-inn': {
    size: 16,
    palette: ['', '#c9bfa8', '#e3dbc6', '#6b6152', '#7a4a26', '#4e2f16', '#2b2b38', '#4a9ad4', '#2b74c4'],
    pixels:
      'BgYGBgYGBgYGBgYGBgYGBgYBAQEBAQEBAQEBAQEBAQYGAQYGBgYGBgYGBgYGBgEGBgEGAgICAgICAgICAgYBBgYBBgIHBwcHBwcHBwIGAQYGAQYCBwgICAgICAcCBgEGBgEGAgICAgICAgICAgYBBgYBBgQEBAQEBAQEBAQGAQYGAQYFBQUFBQUFBQUFBgEGBgEGBAYGBgYGBgYGBAYBBgYBBgQGBgYGBgYGBgQGAQYGAQYGBgYGBgYGBgYGBgEGBgEBAQEBAQEBAQEBAQEBBgYBAQYGBgYGBgYGBgYBAQYGAwMDAwMDAwMDAwMDAwMGBgYGBgYGBgYGBgYGBgYGBg==',
  },
  // なんでも屋の看板 (かなづち)
  'sign-shop': {
    size: 16,
    palette: ['', '#9c9488', '#7d7466', '#c9bfa8', '#e3dbc6', '#6b6152', '#7a4a26', '#4e2f16', '#2b2b38'],
    pixels:
      'CAgICAgICAgICAgICAgICAgDAwMDAwMDAwMDAwMDAwgIAwgICAgICAgICAgICAMICAMIBAQEBAQEBAQEBAgDCAgDCAQEBQUFBQUEBAQIAwgIAwgEBQICAgICBQQECAMICAMIBAUCAgEBAgIFBAQIAwgDCAQFBQUGBQUFBAQECAMIAwgEBAQFBgUEBAQECAMICAMIBAQEBQYFBAQEBAgDCAgDCAQEBAUHBQQEBAQIAwgIAwgICAgICAgICAgICAMICAMDAwMDAwMDAwMDAwMDCAgDAwgICAgICAgICAgDAwgIBQUFBQUFBQUFBQUFBQUICAgICAgICAgICAgICAgICA==',
  },
  // 井戸
  'well': {
    size: 16,
    palette: ['', '#3f9d3f', '#b9b2a4', '#9c9488', '#7d7466', '#7a4a26', '#4a9ad4', '#2b74c4'],
    pixels:
      'AQEBAQEBAQEBAQEBAQEBAQEBAQQEBAQEBAQEBAQBAQEBAQQDAwMDAwMDAwMDBAEBAQQDAgICAgICAgICAgMEAQEEAwIHBwcHBwcHBwIDBAEBBAMCBwYGBgYGBgcCAwQBAQQDAgcGBgYGBgYHAgMEAQEEAwIHBgYGBgYGBwIDBAEBBAMCBwcHBwcHBwcCAwQBAQQDAgICAgICAgICAgMEAQEBBAMDAwMDAwMDAwMEAQEBAQEEBAQFBQQEBAQBAQEBAQEBAQEBBQUBAQEBAQEBAQEBAQEBBAQEBAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ==',
  },
  // 柵
  'fence': {
    size: 16,
    palette: ['', '#3f9d3f', '#7a4a26', '#4e2f16'],
    pixels:
      'AQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAgEBAQECAQEBAQIBAQEBAgMCAgICAwICAgIDAgICAgECAQEBAQIBAQEBAgEBAQEBAgEBAQECAQEBAQIBAQEBAgMCAgICAwICAgIDAgICAgECAQEBAQIBAQEBAgEBAQEBAgEBAQECAQEBAQIBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQ==',
  },
  // 花壇
  'flowers': {
    size: 16,
    palette: ['', '#3f9d3f', '#358535', '#d8617f', '#e89ab0'],
    pixels:
      'AQEBAQEBAQEBAQEBAQEBAQECAQEDAQEBAgEBAQMBAQEBAQEEAwQBAQEBAQQDBAEBAQEBAQMBAQECAQEBAQMBAQECAQEBAQEBAQEBAQEBAgEBAQEDAQEBAgEBAwEBAQEBAQEEAwQBAQEBAQQDBAEBAQEBAQMBAQIBAQEBAwEBAQIBAQEBAQEBAQEBAQEBAQEBAQIBAQEDAQEBAgEBAQMBAQEBAQEEAwQBAQEBAQQDBAEBAQEBAQMBAQEBAQEBAwEBAQIBAQEBAQECAQEBAQEBAQEBAwEBAQIBAQEDAQEBAQIBBAMEAQEBAQEBBAMEAQEBAQEDAQEBAQECAQEDAQEBAQ==',
  },
  // 宝箱 (閉じた) (D-CHEST-001)。床の上に重ねて描くので背景は透明。
  'chest-closed': {
    size: 16,
    palette: ['', '#2b2b38', '#c98a45', '#e8b830', '#a8692c', '#7a4a1e', '#3a2412', '#a87c14', '#4a4436'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAQEBAQEAAAAAAAABAQICAgMDAgICAQEAAAABAgQEBAQDAwQEBAQCAQAAAQQEBAQEAwMEBAQEBAEAAAEFBQUFBQMDBQUFBQUBAAABAQEBAQMDAwMBAQEBAQAAAQQEBAQDBgYDBAQEBAEAAAEEBAQEAwMDAwQEBAQBAAABBAQEBAQDAwQEBAQEAQAAAQQEBAQEAwMEBAQEBAEAAAEFBQUFBQcHBQUFBQUBAAABAQEBAQEBAQEBAQEBAQAAAAgICAgICAgICAgICAAAAAAAAAAAAAAAAAAAAAAAAA==',
  },
  // 宝箱 (開いた) (D-CHEST-001)。床の上に重ねて描くので背景は透明。
  'chest-open': {
    size: 16,
    palette: ['', '#2b2b38', '#c98a45', '#e8b830', '#a8692c', '#7a4a1e', '#a87c14', '#3a2412', '#fff6c0', '#4a4436'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAEBAQEBAQEBAQEAAAAAAAECAgICAwMCAgICAQAAAAABBAQEBAMDBAQEBAEAAAAAAQUFBQUGBgUFBQUBAAAAAQEBAQEBAQEBAQEBAQEAAAEHBwcHBwcHBwcHBwcBAAABBwcHBwMICAMHBwcHAQAAAQICAgICAwMCAgICAgEAAAEEBAQEAwMDAwQEBAQBAAABBAQEBAMHBwMEBAQEAQAAAQQEBAQDAwMDBAQEBAEAAAEFBQUFBQYGBQUFBQUBAAABAQEBAQEBAQEBAQEBAQAAAAkJCQkJCQkJCQkJCQAAAAAAAAAAAAAAAAAAAAAAAA==',
  },
  // キラキラ 1 コマ目 (D-CHEST-001)。床の上に重ねて描くので背景は透明。
  'sparkle-1': {
    size: 16,
    palette: ['', '#fff6c0', '#ffffff', '#bfe8ff'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAABAQIBAQAAAAAAAAAAAAAAAAABAAAAAAAAAAMAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAAAAAAAAAAAAAAAAAwIDAAAAAAAAAAAAAAAAAAADAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==',
  },
  // キラキラ 2 コマ目 (D-CHEST-001)。床の上に重ねて描くので背景は透明。
  'sparkle-2': {
    size: 16,
    palette: ['', '#fff6c0', '#ffffff', '#bfe8ff'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAIAAAAAAAAAAAAAAAAAAQECAQEAAAAAAAAAAAAAAAAAAgAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAMAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAQECAQEAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==',
  },
  // カジノのカウンター (両替・景品。D-CASINO-001)
  'casino-counter': {
    size: 16,
    palette: ['', '#2b2b38', '#c98a45', '#7a4a1e', '#a8323c', '#7a1f2a', '#e8b830', '#4a4436'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQEBAQEBAQEBAQEBAQECAgICAgICAgICAgICAgICAwMDAwMDAwMDAwMDAwMDAwEBAQEBAQEBAQEBAQEBAQEBBAQEBAQEBAQEBAQEBAQBAQQFBgQEBAQEBAQEBgUEAQEEBAQEBAQEBAQEBAQEBAEBBAUGBAQEBAQEBAQGBQQBAQQEBAQEBAQEBAQEBAQEAQEFBQUFBQUFBQUFBQUFBQEBAQEBAQEBAQEBAQEBAQEBBwcHBwcHBwcHBwcHBwcHBw==',
  },
  // スロット台
  'slot-machine': {
    size: 16,
    palette: ['', '#2b2b38', '#e8b830', '#a8323c', '#f4f1ea', '#5fb0d8', '#fff6c0', '#a87c14', '#7a1f2a', '#4a4436'],
    pixels:
      'AAABAQEBAQEBAQEBAQEAAAAAAQICAgICAgICAgIBAAAAAAEDAwMDAwMDAwMDAQAAAAEBAwMDAwMDAwMDAwEBAAABAwQEAQQFBQQBBAQDAQAAAQMEBgEEBQUEAQYEAwEAAAEDBAQBBAUFBAEEBAMBAAABAwMDAwMDAwMDAwMDAQAAAQICAgIHAgIHAgICAgEAAAEDAwMDAwMDAwMDAwMBAAABAwgICAgICAgICAgDAQAAAQMDAwMDAwMDAwMDAwEAAAEDCAgICAgICAgICAMBAAABAQEBAQEBAQEBAQEBAQAAAAkJCQkJCQkJCQkJCQAAAAAAAAAAAAAAAAAAAAAAAA==',
  },
  // ハイローの台 (緑の羅紗に札 2 枚)
  'card-table': {
    size: 16,
    palette: ['', '#2b2b38', '#2f7d4a', '#f4f1ea', '#2d4f8e', '#1f5a35', '#7a4a1e', '#4a4436'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQEBAQEBAQEBAQEBAQEBAQECAgICAgICAgICAgICAgEBAgIBAQEBAQEBAQEBAgIBAQICAQMDAwMDAwMDAQICAQECAgEDBAMDAwMEAwECAgEBAgIBAwMDAwMDAwMBAgIBAQICAQMDBAMDBAMDAQICAQECAgEBAQEBAQEBAQECAgEBAgICAgICAgICAgICAgIBAQUFBQUFBQUFBQUFBQUFAQEBAQEBAQEBAQEBAQEBAQEAAQYAAAAABgYAAAAABgEAAAcHAAAAAAcHAAAAAAcHAA==',
  },
  // モンスターレースの掲示板
  'race-board': {
    size: 16,
    palette: ['', '#2b2b38', '#e8b830', '#2d4f8e', '#5fb0d8', '#d84f8a', '#fff6c0', '#7a4a1e', '#4a4436'],
    pixels:
      'AQEBAQEBAQEBAQEBAQEBAQECAgICAgICAgICAgICAgEBAgMDAwMDAwMDAwMDAwIBAQIDBAQEBQQEBQQEBAMCAQECAwMDAwMDAwMDAwMDAgEBAgMEBAYEBAQEBgQEAwIBAQIDAwMDAwMDAwMDAwMCAQECAwQFBAQEBAQEBQQDAgEBAgMDAwMDAwMDAwMDAwIBAQIDBAQEBgQEBgQEBAMCAQECAwMDAwMDAwMDAwMDAgEBAgICAgICAgICAgICAgIBAQEBAQEBAQEBAQEBAQEBAQAAAAEHAAAAAAAABwEAAAAAAAABBwAAAAAAAAcBAAAAAAAICAgIAAAAAAgICAgAAA==',
  },
  // カジノの看板
  'sign-casino': {
    size: 16,
    palette: ['', '#2b2b38', '#e8b830', '#a87c14', '#a8323c', '#f4f1ea', '#fff6c0'],
    pixels:
      'AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAEBAQEBAQEBAQEBAQAAAAECAgICAwICAwICAgIBAAABAgQEBAQEBAQEBAQCAQAAAQIEBAQEAQEEBAQEAgEAAAECBAQEAQUFAQQEBAIBAAABAgQEAQUGBgUBBAQCAQAAAQIEBAQBBQUBBAQEAgEAAAECBAQEBAEBBAQEBAIBAAABAgQEBAQEBAQEBAQCAQAAAQMCAgICAgICAgICAwEAAAABAQEBAQEBAQEBAQEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA==',
  },
};
